import {
  BrandKit,
  ContactResponse,
  CreateMediaGenerationSettingsRequest,
  MediaAsset,
  MediaGenerationSettingsResponse,
  PresenterProfileResponse,
  Slide,
} from "pages/media/types/";
import type { MediaTemplateResponse } from "types/";
import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "pages/media/constants/";
import { extractTemplatesFromMediaSettings } from "../hooks/useTemplates";
import { mapServerTemplateToTemplateState } from "./mapToTemplateState";
import { mapServerTemplateSlidesToTemplates } from "./mapTemplateSlidesToTemplateState";
import { TemplateState, TemplateType } from "../types";
import {
  DEFAULT_TEMPLATE_STATE,
  TemplateEditorStore,
} from "../store/useTemplateEditorStore";
import { getBrandkitDataFromBrandkit } from ".";

export type RemapTemplatesWithBrandKitDeps = {
  mediaGenerationSettings: MediaGenerationSettingsResponse | undefined;
  mediaAspectRatio: number | undefined;
  templatesData: {
    presenter: PresenterProfileResponse | undefined;
    contact: ContactResponse | undefined;
  };
  defaultPublicFont: MediaAsset | undefined;
  presenterRatio: number | undefined;
  logoRatio: number | undefined;
  storeApi: TemplateEditorStore;
  isFlashProject: boolean;
  slides: Slide[];
  updateMediaGenerationSettings: (args: {
    updatedMediaSettings: Partial<CreateMediaGenerationSettingsRequest>;
    successNotification?: boolean;
    errorNotification?: boolean;
  }) => Promise<unknown>;
};

export const remapTemplatesAndDefaultsWithBrandKit = (
  selectedBrandKit: BrandKit,
  {
    mediaGenerationSettings,
    mediaAspectRatio,
    templatesData,
    defaultPublicFont,
    presenterRatio,
    logoRatio,
    storeApi,
    isFlashProject,
    slides,
    updateMediaGenerationSettings,
  }: RemapTemplatesWithBrandKitDeps
): void => {
  if (!mediaGenerationSettings) return;

  const aspectRatio = mediaAspectRatio ?? DEFAULT_MEDIA_VIDEO_ASPECT_RATIO;
  const freshBrandKitData = getBrandkitDataFromBrandkit(selectedBrandKit);

  const commonAssets = {
    brandKitData: freshBrandKitData,
    initialPresenter: templatesData.presenter,
    defaultPublicFont,
    contact: templatesData.contact,
    presenterRatio,
    logoRatio,
  };

  const state = storeApi.getState();
  let remappedTemplates: Map<string, TemplateState>;
  let defaultChapter: TemplateState;
  let defaultTransition: TemplateState;

  if (isFlashProject) {
    // Flash projects key state.templates by slide name (UUID), not template id —
    // mirror useTemplates' flash branch: slide-based mapping for slide templates,
    // chapter-style mapping only for intro/closing.
    const data: MediaTemplateResponse[] = Array.from(
      state.serverTemplates?.entries() ?? []
    ).map(([id, template]) => ({ id, template }));

    remappedTemplates = mapServerTemplateSlidesToTemplates(
      data,
      slides,
      aspectRatio,
      commonAssets
    );

    const addSliceTemplate = (
      templateId: string | null | undefined,
      type: TemplateType
    ) => {
      if (!templateId) return;
      const serverTemplate = state.serverTemplates?.get(templateId);
      if (!serverTemplate) return;
      remappedTemplates.set(
        templateId,
        mapServerTemplateToTemplateState(
          serverTemplate,
          type,
          aspectRatio,
          commonAssets
        )
      );
    };
    addSliceTemplate(
      mediaGenerationSettings.introduction_template_id,
      TemplateType.INTRODUCTION
    );
    addSliceTemplate(
      mediaGenerationSettings.closing_template_id,
      TemplateType.CLOSING
    );

    // Flash uses static defaults for chapter/transition (not brand-kit-derived).
    defaultChapter = DEFAULT_TEMPLATE_STATE;
    defaultTransition = DEFAULT_TEMPLATE_STATE;
  } else {
    const templateIdsAndTypes = extractTemplatesFromMediaSettings(
      mediaGenerationSettings
    );
    remappedTemplates = new Map<string, TemplateState>();
    state.templates.forEach((_, templateId) => {
      const serverTemplate = state.serverTemplates?.get(templateId);
      const templateType =
        templateIdsAndTypes[templateId] ?? TemplateType.CHAPTER;
      remappedTemplates.set(
        templateId,
        mapServerTemplateToTemplateState(
          serverTemplate,
          templateType,
          aspectRatio,
          commonAssets
        )
      );
    });

    defaultChapter = mapServerTemplateToTemplateState(
      undefined,
      TemplateType.CHAPTER,
      aspectRatio,
      commonAssets
    );
    defaultTransition = mapServerTemplateToTemplateState(
      undefined,
      TemplateType.TRANSITION,
      aspectRatio,
      commonAssets
    );
  }

  // Preserve the user's logo placement across a brand-kit switch by keeping
  // the previous height and visual center, then recalculating width (from the
  // new logo's ratio) and position so the logo doesn't snap back to the
  // template default or visually shift when the new logo's aspect differs.
  if (logoRatio !== undefined) {
    remappedTemplates.forEach((newTemplate, id) => {
      const previous = state.templates.get(id);
      if (!previous?.logo.assetId) return;

      let height = previous.logo.size.height;
      let width = (height * logoRatio) / aspectRatio;
      if (width > 1) {
        width = 1;
        height = aspectRatio / logoRatio;
      }

      const centerX =
        previous.logo.position.x + previous.logo.size.width / 2;
      const centerY =
        previous.logo.position.y + previous.logo.size.height / 2;
      const x = Math.min(Math.max(centerX - width / 2, 0), 1 - width);
      const y = Math.min(Math.max(centerY - height / 2, 0), 1 - height);

      remappedTemplates.set(id, {
        ...newTemplate,
        logo: {
          ...newTemplate.logo,
          showLogo: previous.logo.showLogo,
          size: { width, height },
          position: { x, y },
          layerIndex: previous.logo.layerIndex,
        },
      });
    });
  }

  state.remapBrandKit({
    defaultChapter,
    defaultTransition,
    defaultIntro: mapServerTemplateToTemplateState(
      undefined,
      TemplateType.INTRODUCTION,
      aspectRatio,
      commonAssets
    ),
    defaultClosing: mapServerTemplateToTemplateState(
      undefined,
      TemplateType.CLOSING,
      aspectRatio,
      commonAssets
    ),
    remappedTemplates,
  });

  const closingWasRemoved =
    !selectedBrandKit.assets.some((asset) => asset.purpose === "Closing") &&
    !!mediaGenerationSettings.closing_template_id;
  const introWasRemoved =
    !selectedBrandKit.assets.some(
      (asset) => asset.purpose === "Introduction"
    ) && !!mediaGenerationSettings.introduction_template_id;

  if (!closingWasRemoved && !introWasRemoved) return;

  const updatedMediaSettings = { ...mediaGenerationSettings };
  if (closingWasRemoved) updatedMediaSettings.closing_template_id = null;
  if (introWasRemoved) updatedMediaSettings.introduction_template_id = null;

  updateMediaGenerationSettings({ updatedMediaSettings }).catch((error) => {
    console.error(error);
  });
};
